import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, TextField, Autocomplete, IconButton, Typography, Alert, Button, CircularProgress, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { Send, CameraAlt, Videocam, FlashOn, FlashOff, PhotoCamera, QrCodeScanner } from "@mui/icons-material";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat } from "@zxing/library";

// A hardware 2D scanner (NETUM and similar — 2.4G/Bluetooth/USB) is a
// keyboard-wedge device: the OS sees it as a keyboard, so it just "types"
// the decoded value into whichever field has focus and sends an Enter
// (occasionally Tab) as a suffix keystroke. It needs no camera, no ZXing
// decode loop, and is effectively instant — so it's offered as its own
// input mode rather than being shoehorned into the camera fallback field.
// Human typing rarely sustains under ~50ms/character; a wedge scanner
// injects the whole decoded string in a few ms total, so a simple average-
// gap-per-character check reliably tells the two apart without needing any
// explicit "which device did this" signal from the browser.
const DEVICE_SCAN_MS_PER_CHAR_THRESHOLD = 25;

// The full realistic set of formats a shipping label can carry — carrier
// tracking barcodes are usually Code128/GS1-128, but labels also commonly
// include ITF, PDF417 (FedEx SmartPost often prints one near the address
// block), DataMatrix, and occasionally QR/EAN/UPC/Codabar/RSS-14 (GS1
// DataBar). Previously the reader was constructed with NO hints at all,
// which — combined with low default camera resolution — made dense codes
// (long Code128/GS1-128 strings, PDF417) fail to decode even though the
// format itself is technically supported.
const HINTS = new Map();
HINTS.set(DecodeHintType.POSSIBLE_FORMATS, [
  BarcodeFormat.CODE_128,
  BarcodeFormat.CODE_39,
  BarcodeFormat.CODE_93,
  BarcodeFormat.CODABAR,
  BarcodeFormat.ITF,
  BarcodeFormat.PDF_417,
  BarcodeFormat.DATA_MATRIX,
  BarcodeFormat.QR_CODE,
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.RSS_14,
]);
// Trades scan speed for accuracy — the default (false) is tuned for fast
// continuous video decoding and skips work that dense/rotated/small codes
// need to resolve reliably.
HINTS.set(DecodeHintType.TRY_HARDER, true);

// Explicit high-resolution rear-camera request with continuous autofocus —
// `decodeFromVideoDevice(undefined, ...)` (the old approach) leaves
// resolution at whatever the browser defaults to, often 640x480, nowhere
// near enough detail to resolve a dense barcode at real scanning distance.
const VIDEO_CONSTRAINTS = {
  video: {
    facingMode: { ideal: "environment" },
    width: { ideal: 3840 },
    height: { ideal: 2160 },
    advanced: [{ focusMode: "continuous" }],
  },
};

// Camera-based continuous barcode scanner — see HINTS/VIDEO_CONSTRAINTS
// above for the full set of formats/resolution this is tuned to actually
// resolve on real (often dense) carrier shipping labels. Renders its own
// live preview + a manual-entry fallback for damaged barcodes or denied
// camera permission — callers just get a stream of
// decoded/typed values via onDetect(value, source), one at a time, already
// de-duplicated — source is "scan" (camera/barcode) or "manual" (typed
// fallback), so callers can tell the customer/admin which one actually
// happened.
// Short synthesized beep (Web Audio API, no audio asset needed) — mimics
// the confirmation beep on a physical barcode scanner so a fast-moving
// admin gets audible feedback without having to watch the screen for every
// single scan.
const playBeep = () => {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = "square";
    oscillator.frequency.value = 1800;
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.12);
    oscillator.onended = () => ctx.close();
  } catch {
    // Best-effort — a failed beep (unsupported browser, autoplay policy,
    // etc.) shouldn't block the scan itself.
  }
};

const BarcodeScanner = ({ onDetect, active = true, hint, onSearchSuggestions }) => {
  // "device" (a keyboard-wedge hardware scanner, or manual typing into the
  // same field) is the default — it needs no camera/permissions at all and
  // is effectively instant, so it's the right default for a fixed scan
  // station. "camera" is offered for anyone without a hardware scanner
  // handy (e.g. scanning from a phone).
  const [mode, setMode] = useState("device");
  const deviceInputRef = useRef(null);
  // Timestamps of the field's first and most recent keystroke this entry —
  // used to tell a hardware scanner's near-instant keyboard-wedge burst
  // apart from a human actually typing (see DEVICE_SCAN_MS_PER_CHAR_THRESHOLD
  // above). Reset once the field goes back to empty.
  const firstKeyTimeRef = useRef(null);
  const lastKeyTimeRef = useRef(null);

  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  // Last CAMERA-decoded value (not time-based) — a continuous scan loop
  // re-decodes the same physical barcode many times a second for as long
  // as it sits in view. A short time-based cooldown here used to let it
  // re-fire (and re-alert the parent) every time the cooldown expired
  // while the admin was still positioning/holding the box, producing a
  // repeating "scanned" / "already scanned" toast loop. Suppressing the
  // identical value entirely — reset only once a DIFFERENT code is
  // decoded — means one physical scan fires exactly once, no matter how
  // long that box stays under the camera.
  const lastScanRef = useRef(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const [manualValue, setManualValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  // Ref, not a dependency — `onSearchSuggestions` is typically a fresh
  // inline function on every parent render, and including it directly in
  // the debounce effect's deps would reset the debounce timer on every
  // unrelated re-render, not just when the admin actually types.
  const onSearchSuggestionsRef = useRef(onSearchSuggestions);
  onSearchSuggestionsRef.current = onSearchSuggestions;
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [captureMissed, setCaptureMissed] = useState(false);
  // The camera is never started implicitly — it only opens once the admin
  // explicitly taps "Enable Camera". Besides being the honest/expected UX
  // (a page shouldn't silently ask for camera access the instant it loads),
  // starting getUserMedia from a real click/tap is also what mobile Safari
  // and other browsers require for the permission prompt to reliably fire
  // at all.
  const [started, setStarted] = useState(false);

  const handleDetect = useCallback(
    (text, source) => {
      const value = (text || "").trim();
      if (!value) return;
      // Only the continuous camera loop needs de-duplicating — a manual
      // typed submission is always a deliberate, one-off action (e.g.
      // intentionally re-entering the same tracking number), so it
      // shouldn't be silently swallowed just because the camera happened
      // to see that same code earlier in this session.
      if (source === "scan") {
        if (lastScanRef.current === value) return;
        lastScanRef.current = value;
      }
      if (navigator.vibrate) navigator.vibrate(80);
      playBeep();
      onDetect(value, source);
    },
    [onDetect],
  );

  // Manual-entry suggestions — a camera/hardware scanner sometimes can't
  // read a damaged or dirty barcode at all, so the admin needs to find the
  // right item by typing part of the tracking number. Only active when a
  // caller actually passes onSearchSuggestions (e.g. the Scan In/Out page);
  // otherwise the manual field stays a plain input, same as before.
  useEffect(() => {
    if (!onSearchSuggestionsRef.current) return undefined;
    const q = manualValue.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return undefined;
    }
    setSuggestionsLoading(true);
    const timer = setTimeout(() => {
      onSearchSuggestionsRef
        .current(q)
        .then((results) => setSuggestions(results || []))
        .catch(() => setSuggestions([]))
        .finally(() => setSuggestionsLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [manualValue]);

  // Selecting a suggestion (click, or Enter while it's highlighted) submits
  // immediately, same as a completed scan — the admin has just confirmed
  // which item they meant, no reason to make them press Send again too.
  const handleSuggestionSelected = (option) => {
    if (!option) return;
    const value = typeof option === "string" ? option : option.trackingNumber;
    if (!value) return;
    handleDetect(value, "manual");
    setManualValue("");
    setSuggestions([]);
    firstKeyTimeRef.current = null;
    lastKeyTimeRef.current = null;
    requestAnimationFrame(() => deviceInputRef.current?.focus());
  };

  useEffect(() => {
    if (!active || !started || mode !== "camera") return undefined;

    let cancelled = false;
    const reader = new BrowserMultiFormatReader(HINTS);

    reader
      .decodeFromConstraints(VIDEO_CONSTRAINTS, videoRef.current, (result) => {
        if (result) handleDetect(result.getText(), "scan");
      })
      .then((controls) => {
        if (cancelled) {
          controls.stop();
          return;
        }
        controlsRef.current = controls;
        setReady(true);
        setError(null);

        // Torch (flashlight) support — genuinely helps resolve dense
        // codes in poor warehouse lighting. Only some devices/browsers
        // expose this; check the actual video track's capabilities rather
        // than assuming.
        try {
          const track = videoRef.current?.srcObject?.getVideoTracks?.()[0];
          const capabilities = track?.getCapabilities?.();
          setTorchSupported(!!capabilities?.torch);
        } catch {
          setTorchSupported(false);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err?.name === "NotAllowedError"
            ? "Camera access was denied. Allow camera access, or enter tracking numbers manually below."
            : "Couldn't start the camera. Enter tracking numbers manually below.",
        );
      });

    return () => {
      cancelled = true;
      controlsRef.current?.stop();
      controlsRef.current = null;
      setReady(false);
      setTorchOn(false);
      setTorchSupported(false);
    };
  }, [active, started, mode, handleDetect]);

  const toggleTorch = async () => {
    try {
      const track = videoRef.current?.srcObject?.getVideoTracks?.()[0];
      if (!track) return;
      const next = !torchOn;
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch {
      // Best-effort — some browsers report torch as a capability but still
      // reject applyConstraints; not worth surfacing an error for this.
    }
  };

  // Grabs ONE frame at the camera's true maximum resolution and runs a
  // full, unhurried decode against it — the fallback for dense/small
  // codes (long Code128/GS1-128 strings, PDF417) that the continuous
  // video loop can't resolve: live decoding works frame-by-frame under a
  // tight time budget on a compressed, often motion-blurred 1080p/4K
  // video stream, while a still photo has neither of those handicaps and
  // — via ImageCapture, where supported — can pull a resolution well
  // above the video stream's (many phone cameras shoot 12MP+ stills vs. a
  // 4K/8MP video feed).
  const captureAndDecode = async () => {
    const track = videoRef.current?.srcObject?.getVideoTracks?.()[0];
    if (!track || !videoRef.current) return;

    setCapturing(true);
    setCaptureMissed(false);
    try {
      let bitmapSource;
      if (typeof window.ImageCapture === "function") {
        try {
          const imageCapture = new window.ImageCapture(track);
          bitmapSource = await imageCapture.takePhoto();
        } catch {
          bitmapSource = null; // fall through to the canvas-grab below
        }
      }

      const canvas = document.createElement("canvas");
      if (bitmapSource) {
        const bitmap = await createImageBitmap(bitmapSource);
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        canvas.getContext("2d").drawImage(bitmap, 0, 0);
      } else {
        // No ImageCapture support (e.g. iOS Safari) — grab the current
        // frame straight off the <video> element at its native stream
        // resolution instead (still notably sharper than a live-decode
        // attempt, since there's no per-frame time pressure here).
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        canvas.getContext("2d").drawImage(videoRef.current, 0, 0);
      }

      const reader = new BrowserMultiFormatReader(HINTS);
      const toImage = (c) =>
        new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = c.toDataURL("image/png");
        });

      // Try the full frame first, then a center crop matching the on-
      // screen guide box — a tighter crop discards background/label
      // clutter around the barcode, which helps the decoder when the
      // full-frame attempt fails to lock onto the right region.
      const attempts = [canvas];
      const cropW = canvas.width * 0.78;
      const cropH = canvas.height * 0.38;
      const cropX = (canvas.width - cropW) / 2;
      const cropY = (canvas.height - cropH) / 2;
      const cropCanvas = document.createElement("canvas");
      cropCanvas.width = cropW;
      cropCanvas.height = cropH;
      cropCanvas
        .getContext("2d")
        .drawImage(canvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
      attempts.push(cropCanvas);

      let decoded = null;
      for (const attemptCanvas of attempts) {
        try {
          const img = await toImage(attemptCanvas);
          const result = await reader.decodeFromImageElement(img);
          if (result) {
            decoded = result;
            break;
          }
        } catch {
          // NotFoundException on this attempt — try the next crop, if any.
        }
      }

      if (decoded) {
        handleDetect(decoded.getText(), "scan");
      } else {
        setCaptureMissed(true);
      }
    } catch {
      setCaptureMissed(true);
    } finally {
      setCapturing(false);
    }
  };

  // The scanner injects keystrokes far faster than a human can type — if
  // the average gap between the first and last keystroke of this entry is
  // under the threshold, treat it as a hardware-scanner read rather than
  // manual typing, so callers (and their logs/toasts) can tell the two
  // apart without needing any explicit signal from the device itself.
  const handleDeviceInputChange = (e) => {
    const value = e.target.value;
    const now = performance.now();
    if (manualValue.length === 0) firstKeyTimeRef.current = now;
    lastKeyTimeRef.current = now;
    setManualValue(value);
  };

  const submitManual = (e) => {
    e.preventDefault();
    const value = manualValue.trim();
    if (!value) return;

    const chars = value.length;
    const elapsed =
      firstKeyTimeRef.current != null && lastKeyTimeRef.current != null
        ? lastKeyTimeRef.current - firstKeyTimeRef.current
        : Infinity;
    const msPerChar = chars > 1 ? elapsed / (chars - 1) : Infinity;
    const source = msPerChar < DEVICE_SCAN_MS_PER_CHAR_THRESHOLD ? "device" : "manual";

    handleDetect(value, source);
    setManualValue("");
    firstKeyTimeRef.current = null;
    lastKeyTimeRef.current = null;
    // Re-focus immediately so the next scan can go straight into the field
    // with no click needed — the whole point of a fixed scan station.
    requestAnimationFrame(() => deviceInputRef.current?.focus());
  };

  // Autofocus on mount/mode switch, and keep the field re-focused whenever
  // it loses focus for any reason other than the admin deliberately
  // interacting with something else on the page (a button, another field).
  useEffect(() => {
    if (mode !== "device") return undefined;
    deviceInputRef.current?.focus();

    const refocus = (e) => {
      const target = e.target;
      const isOwnInput = target === deviceInputRef.current;
      const isInteractive = target.closest?.("button, a, input, textarea, select, [role='button']");
      if (!isOwnInput && !isInteractive) deviceInputRef.current?.focus();
    };
    document.addEventListener("click", refocus);
    return () => document.removeEventListener("click", refocus);
  }, [mode]);

  // Shared by both the "device" mode field and the "camera" mode fallback
  // field below — renders an Autocomplete (suggestions as the admin types)
  // when a caller passed onSearchSuggestions, otherwise the same plain
  // TextField this always was. `fieldProps` carries only the cosmetic
  // differences between the two placements (size, placeholder, sx).
  const renderManualField = (fieldProps) => {
    const sharedOnKeyDown = (e) => {
      // Enter is the near-universal default suffix keystroke on wedge
      // scanners; Tab is the other common option some units are configured
      // to send instead — handled explicitly here (rather than relying on
      // native form-submit-on-Enter) since Tab would otherwise just move
      // focus away without submitting anything.
      if (e.key === "Tab" && manualValue.trim()) {
        e.preventDefault();
        submitManual(e);
      }
    };

    if (!onSearchSuggestions) {
      return (
        <TextField
          inputRef={deviceInputRef}
          fullWidth
          value={manualValue}
          onChange={handleDeviceInputChange}
          onKeyDown={sharedOnKeyDown}
          {...fieldProps}
        />
      );
    }

    return (
      <Autocomplete
        freeSolo
        fullWidth
        options={suggestions}
        loading={suggestionsLoading}
        filterOptions={(x) => x} // server already filtered — don't re-filter client-side
        getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.trackingNumber)}
        inputValue={manualValue}
        onInputChange={(e, newValue, reason) => {
          if (reason === "input") handleDeviceInputChange({ target: { value: newValue } });
          else if (reason === "clear") setManualValue("");
        }}
        onChange={(e, newValue) => handleSuggestionSelected(newValue)}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.trackingNumber} sx={{ display: "block !important" }}>
            <Typography variant="body2" fontWeight={700} sx={{ fontFamily: "monospace" }}>
              {option.trackingNumber}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {[option.description, option.customerName, option.currentStatusLabel].filter(Boolean).join(" · ")}
            </Typography>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={deviceInputRef}
            onKeyDown={sharedOnKeyDown}
            {...fieldProps}
          />
        )}
      />
    );
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(e, next) => next && setMode(next)}
        >
          <ToggleButton value="device">
            <QrCodeScanner fontSize="small" sx={{ mr: 0.75 }} />
            Hardware Scanner
          </ToggleButton>
          <ToggleButton value="camera">
            <CameraAlt fontSize="small" sx={{ mr: 0.75 }} />
            Camera
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {mode === "device" && (
        <Box sx={{ maxWidth: 480, mx: "auto" }}>
          <Box
            component="form"
            onSubmit={submitManual}
            sx={{ display: "flex", gap: 1 }}
          >
            {renderManualField({
              autoFocus: true,
              size: "medium",
              placeholder: "Scan a barcode, or type a tracking number",
              sx: { "& input": { fontFamily: "monospace", fontSize: "1.1rem" } },
            })}
            <IconButton type="submit" color="primary" disabled={!manualValue.trim()}>
              <Send />
            </IconButton>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={1.5}>
            {hint || "Point the scanner at the barcode and pull the trigger — this field stays focused automatically."}
          </Typography>
        </Box>
      )}

      {mode === "camera" && (
        <>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 480,
          mx: "auto",
          aspectRatio: "4 / 3",
          bgcolor: "#0A0A0A",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <video
          ref={videoRef}
          muted
          playsInline
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        {!started && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              color: "white",
              px: 2,
              textAlign: "center",
            }}
          >
            <CameraAlt sx={{ fontSize: 32, opacity: 0.7 }} />
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              This page needs camera access to scan barcodes.
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<Videocam />}
              onClick={() => setStarted(true)}
            >
              Enable Camera
            </Button>
          </Box>
        )}
        {started && !ready && !error && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}
          >
            <CameraAlt sx={{ fontSize: 32, opacity: 0.6 }} />
          </Box>
        )}
        {ready && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                width: "78%",
                height: "38%",
                border: "2px solid rgba(255,255,255,0.85)",
                borderRadius: 1.5,
                boxShadow: "0 0 0 2000px rgba(0,0,0,0.25)",
              }}
            />
          </Box>
        )}
        {ready && torchSupported && (
          <IconButton
            onClick={toggleTorch}
            sx={{
              position: "absolute",
              bottom: 10,
              right: 10,
              bgcolor: "rgba(0,0,0,0.55)",
              color: "white",
              "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
            }}
          >
            {torchOn ? <FlashOff fontSize="small" /> : <FlashOn fontSize="small" />}
          </IconButton>
        )}
      </Box>

      {ready && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={capturing ? <CircularProgress size={14} color="inherit" /> : <PhotoCamera />}
            onClick={captureAndDecode}
            disabled={capturing}
          >
            {capturing ? "Reading…" : "Struggling? Capture Photo"}
          </Button>
        </Box>
      )}

      {captureMissed && (
        <Alert severity="info" sx={{ mt: 1.5, maxWidth: 480, mx: "auto" }} onClose={() => setCaptureMissed(false)}>
          Couldn't find a code in that photo — line the barcode up inside the frame, hold steady, and try again.
        </Alert>
      )}

      {error && (
        <Alert severity="warning" sx={{ mt: 1.5, maxWidth: 480, mx: "auto" }}>
          {error}
        </Alert>
      )}

      {hint && (
        <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={1.5}>
          {hint}
        </Typography>
      )}

      <Box
        component="form"
        onSubmit={submitManual}
        sx={{ display: "flex", gap: 1, mt: 1.5, maxWidth: 480, mx: "auto" }}
      >
        {renderManualField({
          size: "small",
          placeholder: "Or type tracking number manually",
        })}
        <IconButton type="submit" color="primary" disabled={!manualValue.trim()}>
          <Send />
        </IconButton>
      </Box>
        </>
      )}
    </Box>
  );
};

export default BarcodeScanner;
